import { useCallback, useEffect, useRef, useState } from 'react';
import { getFCMToken, listenToForeground } from '@/utils/firebase';
import { notificationService } from '@/data/notificationService';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export const ORDERS_REFRESH_EVENT = 'admin:orders-refresh';

// Short two-tone chime via WebAudio so we don't need a bundled asset.
const playOrderSound = () => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    [880, 1174.66].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.3, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.32);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {
    // Sound is best-effort only
  }
};

const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator;

export function useAdminPush() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [needsRegistration, setNeedsRegistration] = useState(
    () => isPushSupported() && Notification.permission === 'default'
  );

  const [needsForeground, setNeedsForeground] = useState(false);
  const needsPermission =
    isPushSupported() &&
    Notification.permission !== 'denied' &&
    (needsRegistration || needsForeground);

  const ensureForeground = useRef<(() => Promise<boolean>) | undefined>(
    undefined
  );

  const registerToken = useCallback(async () => {
    try {
      const token = await getFCMToken();
      if (token) {
        await notificationService.registerFcmToken(token);
        return true;
      }
    } catch {
      // Silent fail — push stays disabled
    }
    return false;
  }, []);

  // Called from the permission banner (must run in a user gesture).
  const enablePush = useCallback(async () => {
    if (!isPushSupported()) return false;
    try {
      if (Notification.permission === 'denied') {
        setNeedsRegistration(false);
        return false;
      }
      const permission =
        Notification.permission === 'granted'
          ? 'granted'
          : await Notification.requestPermission();
      if (permission !== 'granted') {
        setNeedsRegistration(permission === 'default');
        return false;
      }
      const registered = await registerToken();
      const ready = registered && (await ensureForeground.current?.()) === true;
      setNeedsRegistration(!registered);
      return ready;
    } catch {
      setNeedsRegistration(true);
      return false;
    }
  }, [registerToken]);

  useEffect(() => {
    let active = true;
    if (isPushSupported() && Notification.permission === 'granted') {
      void registerToken().then((registered) => {
        if (active) setNeedsRegistration(!registered);
      });
    }
    return () => {
      active = false;
    };
  }, [registerToken]);

  useEffect(() => {
    let disposed = false;
    let unsubForeground: (() => void) | undefined;

    let pending: Promise<boolean> | undefined;
    let attempt = 0;

    const subscribe = async () => {
      if (disposed || !isPushSupported()) return false;
      const currentAttempt = ++attempt;
      try {
        const unsubscribe = await listenToForeground((payload) => {
          if (disposed || currentAttempt !== attempt) return;
          const data = payload?.data ?? {};
          if (data.process === 'BUY_PRODUCTS') {
            playOrderSound();
            enqueueSnackbar(
              payload.notification?.title || t('notification.push.newOrder'),
              { variant: 'success' }
            );
            window.dispatchEvent(
              new CustomEvent(ORDERS_REFRESH_EVENT, { detail: data })
            );
            return;
          }
          enqueueSnackbar(
            payload.notification?.title ||
              t('notification.push.newNotification'),
            {
              variant: 'info'
            }
          );
        });
        if (disposed) {
          unsubscribe?.();
          return false;
        }
        unsubForeground = unsubscribe;
        if (!unsubscribe) attempt += 1;
        setNeedsForeground(!unsubscribe);
        return Boolean(unsubscribe);
      } catch {
        attempt += 1;
        if (!disposed) setNeedsForeground(true);
        return false;
      }
    };

    const ensureSubscribed = () => {
      if (disposed) return Promise.resolve(false);
      if (unsubForeground) return Promise.resolve(true);
      if (!pending) {
        pending = subscribe().finally(() => {
          pending = undefined;
        });
      }
      return pending;
    };
    ensureForeground.current = ensureSubscribed;
    // Failed setup is retried only by an explicit enable action.
    void ensureSubscribed();

    return () => {
      disposed = true;
      if (ensureForeground.current === ensureSubscribed) {
        ensureForeground.current = undefined;
      }
      unsubForeground?.();
    };
  }, [enqueueSnackbar, t]);

  return { needsPermission, enablePush };
}
